import {
  AUTH_ACTIONS_CLASS,
  AUTH_CONTAINER_CLASS,
  AUTH_FIELDS_CLASS,
  AuthPage,
  AuthPrimaryButton,
  AuthTextField,
} from "@/components/auth";
import { Button, useToast } from "@/components/ui";

import { Form, FormRootError } from "@/components/forms";
import { normalizedEmailSchema } from "@/lib/validation/auth";
import { useStore } from "@/store";
import { handleFormError } from "@/utils/formErrorUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";

const editEmailSchema = z.object({
  email: normalizedEmailSchema,
});

type TEditEmailFormValues = z.infer<typeof editEmailSchema>;

export const EditEmail = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const requestEmailChange = useStore.use.requestEmailChange();

  const form = useForm<TEditEmailFormValues>({
    resolver: zodResolver(editEmailSchema),
    defaultValues: { email: "" },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const onSubmit = form.handleSubmit(async (payload) => {
    form.clearErrors();

    const nextEmail = payload.email.trim();
    if (!nextEmail) {
      form.setError("email", { type: "manual", message: "Email address is required" });
      return;
    }

    try {
      const result = await requestEmailChange(nextEmail);

      if (!result.success) {
        if (result.errorObject) {
          await handleFormError(result.errorObject, form);
        } else {
          form.setError("root", {
            type: "server",
            message: result.error ?? "We couldn't request an email change. Please try again.",
          });
        }
        return;
      }

      toast({
        variant: "success",
        title: "Verification code sent",
        description:
          result.message ??
          "If the email address can be used, a verification code has been sent to your new address.",
      });

      void navigate({
        to: "/auth/verify-otp",
        search: {
          mode: "email",
          email: nextEmail,
          phone: undefined,
          from: "/app/settings",
        },
      });
    } catch (err) {
      console.error("[auth-email] Failed to request email change", { error: err });
      toast({
        variant: "destructive",
        title: "Failed to request email change",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  });

  return (
    <Form {...form}>
      <div className={AUTH_CONTAINER_CLASS}>
        <form
          className="grid gap-6"
          onSubmit={(e) => {
            void onSubmit(e);
          }}
        >
          <AuthPage
            title="Change email"
            subtitle="Enter your new email address below to receive a one-time password (OTP)"
          >
            <div className={AUTH_FIELDS_CLASS}>
              <FormRootError />

              <AuthTextField
                control={form.control}
                name="email"
                label="New email"
                inputProps={{ type: "email", placeholder: "Enter your new email address" }}
              />

              <div className={`${AUTH_ACTIONS_CLASS} flex gap-3`}>
                <AuthPrimaryButton
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Update
                </AuthPrimaryButton>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-10"
                  onClick={() => {
                    void navigate({ to: "/app/settings" });
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </AuthPage>
        </form>
      </div>
    </Form>
  );
};
